import { X } from "lucide-react";
import { useReducedMotion } from "motion/react";
import { createContext, useContext, type ComponentProps, type ReactNode } from "react";
import { Drawer } from "@/components/animate-ui";
import { enterTransition, exitTransition } from "@/lib/motion";
import { cn } from "@/lib/utils";

export type SheetSide = "left" | "right" | "bottom";

const SheetSideContext = createContext<SheetSide>("right");

const sideClass: Record<SheetSide, string> = {
  right: "fixed top-0 right-0 z-50 h-full w-[min(24rem,100vw)] rounded-l-surface",
  left: "fixed top-0 left-0 z-50 h-full w-[min(24rem,100vw)] rounded-r-surface",
  bottom: "fixed inset-x-0 bottom-0 z-50 max-h-[min(85vh,40rem)] rounded-t-surface",
};

export function Sheet({
  side = "right",
  open,
  defaultOpen,
  onOpenChange,
  children,
}: {
  side?: SheetSide;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
}) {
  return (
    <SheetSideContext.Provider value={side}>
      <Drawer.Root direction={side} open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange} autoFocus>
        {children}
      </Drawer.Root>
    </SheetSideContext.Provider>
  );
}

export function DrawerSheet({
  children,
  ...props
}: Omit<Parameters<typeof Sheet>[0], "side">) {
  return (
    <Sheet side="bottom" {...props}>
      {children}
    </Sheet>
  );
}

export const SheetTrigger = Drawer.Trigger;
export const SheetClose = Drawer.Close;

type FocusHooks = Pick<ComponentProps<typeof Drawer.Content>, "onOpenAutoFocus" | "onCloseAutoFocus">;

export function SheetContent({
  title,
  description,
  children,
  className,
  ...focus
}: {
  title: string;
  description?: string;
  children?: ReactNode;
  className?: string;
} & FocusHooks) {
  const side = useContext(SheetSideContext);
  const reduce = useReducedMotion();
  return (
    <Drawer.Portal>
      <Drawer.Overlay
        className="fixed inset-0 z-50 bg-[oklch(0%_0_0_/_0.42)]"
        transition={enterTransition(reduce)}
        exitTransition={exitTransition(reduce)}
      />
      <Drawer.Content
        {...focus}
        data-slot="sheet"
        transition={reduce ? { duration: 0 } : enterTransition(reduce)}
        exitTransition={exitTransition(reduce)}
        className={cn(
          sideClass[side],
          "flex flex-col border border-line-subtle bg-surface-raised p-6 text-foreground shadow-floating outline-none",
          className,
        )}
      >
        {side === "bottom" ? <Drawer.Handle className="mb-4 bg-line-strong" /> : null}
        <div className="flex items-start justify-between gap-4">
          <Drawer.Title className="font-display text-xl tracking-[-0.03em]">{title}</Drawer.Title>
          <Drawer.Close
            className="grid size-11 place-items-center rounded-pill text-secondary can-hover:hover:bg-surface-muted"
            aria-label="Close"
          >
            <X className="size-4" strokeWidth={1.75} />
          </Drawer.Close>
        </div>
        {description ? (
          <Drawer.Description className="mt-2 text-sm text-secondary">{description}</Drawer.Description>
        ) : (
          <Drawer.Description className="sr-only">{title}</Drawer.Description>
        )}
        <div className="mt-5 min-h-0 flex-1 overflow-auto">{children}</div>
      </Drawer.Content>
    </Drawer.Portal>
  );
}
